import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { readRedirectFromQuery, sanitizeRedirectPath } from '../utils/redirectAfterLogin';

export default function Login() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, isAuthenticated, loading: authLoading } = useAuth();
  // Prefer ?redirect= URL param (from email links etc.); fall back to
  // location.state.from (set by ProtectedRoute when bouncing to /login).
  const redirectTo =
    readRedirectFromQuery() ??
    sanitizeRedirectPath((location.state as { from?: string } | null)?.from);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(redirectTo || '/');
    }
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        navigate(redirectTo || '/');
      } else if (result.requiresConfirmation) {
        navigate('/confirm-email', { state: { email, redirect: redirectTo } });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('login.loginFailed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      // OAuth round-trips through Cognito Hosted UI and loses URL state,
      // so the hook persists redirectTo into sessionStorage before redirecting.
      await loginWithGoogle(redirectTo);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Google login failed';
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-warm-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card Container */}
        <div className="bg-white rounded-xl shadow-brand p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-h2 font-bold text-charcoal">
              {t('login.title', 'Sign in to your account')}
            </h2>
            <p className="mt-3 text-body text-charcoal/70">
              {t('login.noAccount', "Don't have an account?")}{' '}
              <Link
                to={
                  redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'
                }
                className="font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                {t('login.signUp', 'Sign up')}
              </Link>
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-2">
                {t('login.email', 'Email address')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('login.emailPlaceholder', 'you@example.com')}
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-charcoal mb-2">
                {t('login.password', 'Password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-soft-gray rounded-lg text-charcoal placeholder-charcoal/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow"
                placeholder={t('login.passwordPlaceholder', '••••••••')}
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary focus:ring-primary border-soft-gray rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-charcoal/80">
                  {t('login.rememberMe', 'Remember me')}
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {t('login.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-6 border border-transparent text-body font-semibold rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('login.signingIn', 'Signing in...') : t('login.signIn', 'Sign in')}
            </button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-charcoal/60">
                {t('login.orContinueWith', 'Or continue with')}
              </span>
            </div>
          </div>

          {/* Google Login Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full flex justify-center items-center py-3 px-6 border border-soft-gray rounded-lg text-charcoal bg-white hover:bg-warm-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('login.continueWithGoogle', 'Continue with Google')}
          </button>
        </div>
      </div>
    </div>
  );
}
